import * as React from "react";
import { Checkbox, Input, InputRef, Modal, notification } from "antd";
import { getName, listAndDelete, request } from "../../services/cpln";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg, selectGvc, setGvc } from "../../store/contextSlice";
import { fetchGvcs, fetchHasWorkload } from "../../store/userDataSlice";
import { gvcApi } from "../../store/api/gvcApi";

interface Props {
  links: string[];
  onCancel: () => void;
  onComplete: () => void;
}

export const GvcBulkDeleteModal: React.FC<Props> = ({ links, onCancel, onComplete }) => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const currentGvc = useAppSelector(selectGvc);

  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isForceDelete, setIsForceDelete] = React.useState(false);
  const [progress, setProgress] = React.useState({ current: 0, total: 0, currentName: "" });
  const inputRef = React.useRef<InputRef>(null);

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    if (links.length === 1) {
      return getName(links[0])!;
    }
    return "Selected GVCs";
  }, [links]);

  const canDelete = confirmationText === CONFIRMATION_TOKEN;

  async function confirmDelete() {
    try {
      setIsLoading(true);

      let succeeded = 0;
      let failed = 0;
      let firstError = "";

      if (isForceDelete) {
        // Sequential per-GVC: delete children then GVC
        const total = links.length;
        setProgress({ current: 0, total, currentName: "" });

        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          const gvcName = getName(link)!;
          setProgress({ current: i + 1, total, currentName: gvcName });

          try {
            const context = { org: org, gvc: gvcName };
            await listAndDelete("workload", context);
            await listAndDelete("volumeset", context);
            await listAndDelete("identity", context);
            await request({ method: "delete", url: link });
            succeeded++;
          } catch (e: any) {
            failed++;
            if (!firstError) {
              firstError = e?.response?.data?.message || e.message;
            }
          }
        }
      } else {
        // Parallel deletion (same as generic DeleteModal)
        const results = await Promise.allSettled(
          links.map((link) => request({ method: "delete", url: link })),
        );

        for (const result of results) {
          if (result.status === "fulfilled") {
            succeeded++;
          } else {
            failed++;
            if (!firstError) {
              firstError = result.reason?.response?.data?.message || result.reason?.message || "Unknown error";
            }
          }
        }
      }

      // Invalidate RTK Query cache for deleted GVCs
      dispatch(gvcApi.util.invalidateTags(links.map((link) => ({ type: "Gvc" as const, id: link }))));

      // Post-delete: refresh store data
      await dispatch(fetchGvcs());
      await dispatch(fetchHasWorkload());

      // Clear context GVC if it was among the deleted
      if (currentGvc && links.some((link) => getName(link) === currentGvc)) {
        dispatch(setGvc(""));
      }

      if (succeeded > 0) {
        notification.success({
          message: "Success",
          description: `Deleted ${succeeded} GVC${succeeded > 1 ? "s" : ""}`,
        });
      }

      if (failed > 0) {
        notification.warning({
          message: `Failed to delete ${failed} GVC${failed > 1 ? "s" : ""}`,
          description: firstError,
        });
      }

      if (succeeded > 0) {
        onComplete();
      }
    } catch (e: any) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to delete",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Modal
      title={
        <>
          Delete GVC{links.length > 1 ? "s" : ""}:{" "}
          <span className="font-bold" style={{ color: "var(--color-danger)" }}>
            {links.length === 1 ? getName(links[0]) : `${links.length} Items`}
          </span>
        </>
      }
      open={true}
      maskClosable={false}
      onCancel={onCancel}
      onOk={confirmDelete}
      okText="Delete"
      cancelButtonProps={{ disabled: isLoading, type: "default", danger: false }}
      okButtonProps={{ disabled: isLoading || !canDelete, loading: isLoading, type: "primary", danger: true }}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open && inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <div className="flex flex-col gap-2">
        <span>
          Are you sure you want to delete {links.length > 1 ? "these" : "this"} GVC{links.length > 1 ? "s" : ""}? This
          action cannot be undone.
        </span>
        <div className="mb-2">
          <div className="flex items-center">
            <Checkbox
              checked={isForceDelete}
              onChange={(e) => setIsForceDelete(e.target.checked)}
              disabled={isLoading}
              data-testid="gvc-force-delete"
            >
              Force Delete
            </Checkbox>
          </div>
          <div className="text-sm">
            If this is enabled, Control Plane will force delete volume sets and their volumes before deleting the GVC.
          </div>
        </div>
        {isLoading && isForceDelete && progress.total > 1 ? (
          <div className="text-sm" style={{ color: "var(--color-label)" }}>
            Deleting GVC {progress.current} of {progress.total}: {progress.currentName}
          </div>
        ) : null}
        <div className="delete-modal-confirmation-warning mt-4">{`Type "${CONFIRMATION_TOKEN}" for confirming the delete action`}</div>
        <Input
          ref={inputRef}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canDelete && !isLoading) {
              confirmDelete();
            }
          }}
          placeholder="confirmation"
        />
      </div>
    </Modal>
  );
};
